/**
 * The presentation's podium, driven by the live script (TIBBS-VOICE.md 4).
 * Pure: the places come from the final totals (the same slot rules as
 * readout/facts.ts), and each step rises when Tibbs reaches the sentence that
 * names its prize.
 */
import type { GameState } from '../../engine';
import { captionText } from '../../readout/script';

export interface Podium {
  gold: number[];
  silver: number[];
  bronze: number[];
  scenic: number[];
}

/** Places from the regulation totals, with the same rules as readout/facts.ts: the Golden Amphora, the Silver Laurel (place 2), the Bronze (place 3, 4+ players), the Scenic Route (last). */
export function podiumSeats(state: Pick<GameState, 'players' | 'winners'>): Podium {
  const totals = state.players.map((p) => p.holeScores.reduce((a, v) => a + v, 0));
  const n = totals.length;
  const seats = totals.map((_, i) => i);
  // A playoff decides a tied lead: the engine's winners rank first.
  const win = state.winners?.length && state.winners.length < n ? new Set(state.winners) : null;
  const place = (i: number) =>
    win ? (win.has(i) ? 1 : 1 + win.size + seats.filter((x) => !win.has(x) && totals[x]! < totals[i]!).length) : 1 + seats.filter((x) => totals[x]! < totals[i]!).length;
  const losers = win ? seats.filter((i) => !win.has(i)) : seats;
  const worst = Math.max(...losers.map((i) => totals[i]!));
  const allTied = !win && Math.min(...totals) === worst;
  const isLast = (i: number) => !allTied && !win?.has(i) && totals[i] === worst;
  const out: Podium = { gold: [], silver: [], bronze: [], scenic: [] };
  for (const i of seats) {
    const p = place(i);
    if (p === 1) out.gold.push(i);
    else if (isLast(i)) out.scenic.push(i);
    else if (p === 2) out.silver.push(i);
    else if (p === 3 && n >= 4) out.bronze.push(i);
  }
  return out;
}

export interface Reveal {
  crest: boolean;
  scenic: boolean;
  bronze: boolean;
  silver: boolean;
  gold: boolean;
}

const PRIZES: [keyof Omit<Reveal, 'crest'>, RegExp][] = [
  ['scenic', /scenic route|clay tortoise/i],
  ['bronze', /\bbronze\b/i],
  ['silver', /silver laurel|\bsilver\b/i],
  ['gold', /golden amphora|\bamphora\b|champion/i],
];

/**
 * Which steps are up, given the script's sentences so far and the sentence
 * Tibbs is saying. Prizes are found in presentation order, each after the one
 * before, skipping the opener (which names the Golden Amphora).
 */
export function revealAt(sentences: string[], saying: number, done: boolean, podium: Podium): Reveal {
  if (done) return { crest: true, scenic: true, bronze: true, silver: true, gold: true };
  const at: Partial<Record<keyof Reveal, number>> = {};
  let from = Math.min(2, sentences.length);
  for (const [prize, re] of PRIZES) {
    if (!podium[prize].length) continue;
    const i = sentences.findIndex((s, k) => k >= from && re.test(s));
    if (i < 0) continue;
    at[prize] = i;
    from = i + 1;
  }
  const up = (p: keyof Omit<Reveal, 'crest'>) => at[p] !== undefined && saying >= at[p]!;
  return { crest: saying >= 0, scenic: up('scenic'), bronze: up('bronze'), silver: up('silver'), gold: up('gold') };
}

/** Sentences of a script (captions), in order. */
export function scriptSentences(texts: string[]): string[] {
  const out: string[] = [];
  for (const t of texts) for (const p of String(t).match(/[^.!?…]*[.!?…]+["')\]]*\s*|[^.!?…]+$/g) ?? []) {
    const c = captionText(p);
    if (c) out.push(c);
  }
  return out;
}
